import { IField } from 'shared/entities/form';
import NewListModel from 'shared/models/NewListModel';

import { IRootStore } from '../store/rootStore';
import { ILoadingStageModel } from '../loadingStage';
import { LoadingStage } from '../meta';
import { IScenarioData } from '../scenario';
import { AITarget } from '../smartbotAIInfo';
import { IChannelModel } from '../channels';

import { AIAggregatedDocType, AIDocError, AIModelKind } from './server';

export interface ISessionData {
  sessionId: string;
  subscriptionToken: string;
  subscriptionChannel: string;
}

export interface IAIBot {
  readonly id: IField<string>;
  readonly scenarios: NewListModel<IAIBotScenarioData>;
  readonly channelListIds: string[];
  readonly channels: IChannelModel[];
  readonly rootStore: IRootStore;
  readonly name: string;
  readonly params: IField<IAIEducationParams>;
  readonly intentTargetTitle: string | null;
}
export interface IAIEducationParams {
  readonly target: IField<AITarget>;
  readonly testMessagesAvailable: IField<number | null>;
  readonly name: IField<string>;
  readonly aiModelKind: IField<AIModelKind>;
}

export type AIEducationFields = {
  name: string;
  companyDescription: string;
  faq: string;
  limitations: string;
  intentKindText: string;
  contextPrompt: string;
  questionPrompt: string;
};

export interface IAIDoc {
  readonly rootStore: IRootStore;
  readonly renderId: string;
  readonly id: IField<string | null>;
  readonly url: IField<string | null>;
  readonly cost: IField<number | null>;
  readonly title: string;
  readonly removingStage: ILoadingStageModel;
  readonly errors: IField<AIDocError[] | null>;
  readonly progress: IField<number>;
  readonly type: AIAggregatedDocType;

  get withErrors(): boolean;

  get saved(): boolean;

  remove(): Promise<BaseResponse>;
}

export type AIDocModelParams<
  T extends AIAggregatedDocType,
  I extends string | null = string | null
> = {
  rootStore: IRootStore;
  id: I;
  cost: number;
  title: string;
  url: string | null;
  errors: AIDocError[] | null;
  progress: number;
  type: T;
  botId: string;
  dateCreated: Date;
};

export type AIAggregatedDocModelParams<T extends AIAggregatedDocType> = {
  loadingStage: LoadingStage;
  loadingProgress: number;
} & AIDocModelParams<T>;

export type AICommonDocModelParams<T extends AIAggregatedDocType> = {
  dateCreated: Date;
} & AIDocModelParams<T, string>;

export enum AIDocErrorStage {
  indexing = 'indexing',
  loading = 'loading',
  dateCreated = 'dateCreated'
}

export const orderAIModelKind: AIModelKind[] = [
  AIModelKind.gpt54Nano,
  AIModelKind.gpt54Mini,
  AIModelKind.gpt55,
  AIModelKind.o3,
  AIModelKind.gpt51,
  AIModelKind.gpt52,
  AIModelKind.gpt4o,
  AIModelKind.gpt4oMini,
  AIModelKind.yandexGpt,
  AIModelKind.yandexGptLite,
  AIModelKind.gpt41,
  AIModelKind.gpt41Mini,
  AIModelKind.gpt5Nano,
  AIModelKind.gpt41Nano,
  AIModelKind.o1,
  AIModelKind.gpt35
];

export const deprecatedAIModelKinds: Set<AIModelKind> = new Set([
  AIModelKind.gpt35,
  AIModelKind.gpt41Nano,
  AIModelKind.o1,
  AIModelKind.gpt5Nano
]);

export const getSupportedAIModels = (
  currentModel: AIModelKind | null,
  initialModel: AIModelKind | null
): AIModelKind[] => {
  const filtered = orderAIModelKind.filter(
    (model) => !deprecatedAIModelKinds.has(model)
  );

  if (
    currentModel &&
    !filtered.includes(currentModel) &&
    deprecatedAIModelKinds.has(currentModel)
  ) {
    filtered.push(currentModel);
  }

  if (
    initialModel &&
    currentModel !== initialModel &&
    !filtered.includes(initialModel) &&
    deprecatedAIModelKinds.has(initialModel)
  ) {
    filtered.push(initialModel);
  }

  return filtered;
};

export type AIModelEntity = {
  kind: AIModelKind;
  title: string;
  isNew: boolean;
};

export const aiModelEntities: Record<AIModelKind, AIModelEntity> = {
  [AIModelKind.gpt4o]: {
    kind: AIModelKind.gpt4o,
    title: 'ChatGPT-4o',
    isNew: false
  },
  [AIModelKind.gpt4oMini]: {
    kind: AIModelKind.gpt4oMini,
    title: 'ChatGPT-4o mini',
    isNew: false
  },
  [AIModelKind.yandexGpt]: {
    kind: AIModelKind.yandexGpt,
    title: 'YandexGPT Pro',
    isNew: false
  },
  [AIModelKind.yandexGptLite]: {
    kind: AIModelKind.yandexGptLite,
    title: 'YandexGPT Lite',
    isNew: false
  },
  [AIModelKind.gpt41Nano]: {
    kind: AIModelKind.gpt41Nano,
    title: 'ChatGPT-4.1 nano',
    isNew: false
  },
  [AIModelKind.gpt41]: {
    kind: AIModelKind.gpt41,
    title: 'ChatGPT-4.1',
    isNew: false
  },
  [AIModelKind.gpt41Mini]: {
    kind: AIModelKind.gpt41Mini,
    title: 'ChatGPT-4.1 mini',
    isNew: false
  },
  [AIModelKind.o1]: {
    kind: AIModelKind.o1,
    title: 'ChatGPT o1',
    isNew: false
  },
  [AIModelKind.o3]: {
    kind: AIModelKind.o3,
    title: 'ChatGPT o3',
    isNew: true
  },
  [AIModelKind.gpt5Nano]: {
    kind: AIModelKind.gpt5Nano,
    title: 'ChatGPT-5 nano',
    isNew: false
  },
  [AIModelKind.gpt51]: {
    kind: AIModelKind.gpt51,
    title: 'ChatGPT-5.1',
    isNew: false
  },
  [AIModelKind.gpt52]: {
    kind: AIModelKind.gpt52,
    title: 'ChatGPT-5.2',
    isNew: false
  },
  [AIModelKind.gpt55]: {
    kind: AIModelKind.gpt55,
    title: 'ChatGPT-5.5',
    isNew: true
  },
  [AIModelKind.gpt54Mini]: {
    kind: AIModelKind.gpt54Mini,
    title: 'ChatGPT-5.4 mini',
    isNew: true
  },
  [AIModelKind.gpt54Nano]: {
    kind: AIModelKind.gpt54Nano,
    title: 'ChatGPT-5.4 nano',
    isNew: true
  },
  [AIModelKind.gpt35]: {
    kind: AIModelKind.gpt35,
    title: 'ChatGPT-3,5',
    isNew: false
  }
};

export interface IAIBotScenarioData extends IScenarioData {
  channelIds: IField<string[] | null>;
}
